// react/src/helpers/AuthContext.jsx
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

// Create the AuthContext
const AuthContext = createContext({
    fetch: window.fetch, // default to window.fetch
});

export function getDefaultApiUrl(location = window.location) {
    const hostname = location.hostname.startsWith('api.')
        ? location.hostname
        : `api.${location.hostname}`;
    const port = location.port ? `:${location.port}` : '';
    return `${location.protocol}//${hostname}${port}`;
}

// Global state to ensure only one presence request is made
let presenceCache = null;
let presencePromise = null;

// Global state for token management
let tokenCache = null;
let tokenPromise = null;
let tokenExpiresAt = null;

// Admin channel suspension (server docs/ENFORCEMENT-CONTROLS.md): the server answers seller
// requests for a suspended channel with 403 and `code: 'channel_suspended'`. An already open page
// sends the browser to the dashboard, which renders the block page, instead of showing
// per-call errors. Clients key on `code`, never on the sentence.
export const CHANNEL_SUSPENDED_CODE = 'channel_suspended';
// Indirection so tests can observe navigation (jsdom does not implement it).
export const suspensionNavigation = {
    assign: url => window.location.assign(url),
};
let suspensionRedirected = false;

export async function isChannelSuspendedResponse(response) {
    if (!response || response.status !== 403 || typeof response.clone !== 'function') {
        return false;
    }
    try {
        const body = await response.clone().json();
        return body?.code === CHANNEL_SUSPENDED_CODE;
    } catch {
        return false;
    }
}

function redirectToSuspendedDashboard(dashboardUrl) {
    if (suspensionRedirected) return;
    suspensionRedirected = true;
    suspensionNavigation.assign(`${dashboardUrl || ''}/account/dashboard`);
}

// AuthProvider wraps children and provides a wrapped fetch
export function AuthProvider({ children, env = {} }) {
    const DASHBOARD_URL = env.DASHBOARD_URL || '';
    const API_URL = env.API_URL || getDefaultApiUrl();

    const [account, setAccount] = useState(null);
    const [channel, setChannel] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [token, setToken] = useState(null);
    const hasFetched = useRef(false);
    const hasTokenFetched = useRef(false);

    // These helpers are declared innermost-outward (fetchToken -> ensureValidToken ->
    // wrappedFetch -> fetchPresence) so each `useCallback` dependency array can name the
    // sibling it calls without hitting a temporal-dead-zone error at render time. They are
    // all referentially stable: every one of them reads module-level caches, `localStorage`,
    // and state setters rather than closing over `account`/`channel`/`isLoading`/`error`/
    // `token`, so consumers can safely list `fetch` (and friends) as hook dependencies
    // without re-running on unrelated provider state changes.

    const fetchToken = useCallback(async () => {
        // If there's already a token request in progress, wait for it
        if (tokenPromise) {
            return tokenPromise;
        }

        // Check if we have a cached, unexpired token
        const existingToken = localStorage.getItem('authToken');
        const expiresAt = localStorage.getItem('authTokenExpiresAt');

        if (existingToken && expiresAt && Date.now() < parseInt(expiresAt)) {
            tokenCache = existingToken;
            setToken(existingToken);
            return existingToken;
        }

        // Make the token request
        tokenPromise = window.fetch('/account/token', {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            }
        })
        .then(async response => {
            if (!response.ok) {
                if (await isChannelSuspendedResponse(response)) {
                    redirectToSuspendedDashboard(DASHBOARD_URL);
                }
                throw new Error(`Failed to fetch token: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            if (data.data && data.data.token && data.data.token.accessToken) {
                const tokenValue = data.data.token.accessToken;
                // Tokens typically expire in 1 hour, set expiration timestamp
                const expiresAt = Date.now() + (3600 * 1000); // 1 hour from now

                localStorage.setItem('authToken', tokenValue);
                localStorage.setItem('authTokenExpiresAt', expiresAt.toString());

                tokenCache = tokenValue;
                tokenExpiresAt = expiresAt;
                setToken(tokenValue);
                return tokenValue;
            } else {
                throw new Error('Invalid token response format');
            }
        })
        .catch(err => {
            console.error('Failed to fetch token:', err);
            // Clear token state on failure
            localStorage.removeItem('authToken');
            localStorage.removeItem('authTokenExpiresAt');
            tokenCache = null;
            tokenExpiresAt = null;
            setToken(null);
            return null;
        })
        .finally(() => {
            // Reset promise so it can be retried
            tokenPromise = null;
        });

        return tokenPromise;
    }, [DASHBOARD_URL]);

    const ensureValidToken = useCallback(async () => {
        const existingToken = localStorage.getItem('authToken');
        const expiresAt = localStorage.getItem('authTokenExpiresAt');

        // If no token or expired, fetch a new one
        if (!existingToken || !expiresAt || Date.now() >= parseInt(expiresAt)) {
            await fetchToken();
        } else {
            // Token is valid, use cached version
            if (tokenCache !== existingToken) {
                tokenCache = existingToken;
                setToken(existingToken);
            }
        }
    }, [fetchToken]);

    const wrappedFetch = useCallback(async (url, options = {}) => {
        // Check if token is valid, fetch new one if needed
        await ensureValidToken();

        const token = localStorage.getItem('authToken');
        const headers = {
            ...(options.headers || {'Content-Type': 'application/json'}),
            ...(token ? { 'X-Mayhem-Token': `${token}` } : {}),
        };
        // Always include credentials for cross-origin
        const credentials = options.credentials || 'include';
        const response = await window.fetch(url, { ...options, headers, credentials });
        if (await isChannelSuspendedResponse(response)) {
            redirectToSuspendedDashboard(DASHBOARD_URL);
        }
        return response;
    }, [DASHBOARD_URL, ensureValidToken]);

    const fetchPresence = useCallback(async () => {
        // If we already have cached data, use it
        if (presenceCache) {
            setAccount(presenceCache.account);
            setChannel(presenceCache.channel);
            setIsLoading(false);
            return presenceCache;
        }

        // If there's already a request in progress, wait for it
        if (presencePromise) {
            try {
                const result = await presencePromise;
                setAccount(result.account);
                setChannel(result.channel);
                setIsLoading(false);
                return result;
            } catch (err) {
                setError(err);
                setIsLoading(false);
                throw err;
            }
        }

        // Make the request
        presencePromise = wrappedFetch('/account/presence.json', {
            headers: {
                'Accept': 'application/json',
            }
        })
        .then(response => {
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            return response.json();
        })
        .then(data => {
            presenceCache = data;
            return data;
        })
        .catch(err => {
            presencePromise = null; // Reset promise on error so it can be retried
            throw err;
        });

        try {
            const result = await presencePromise;
            setAccount(result.account);
            setChannel(result.channel);
            setIsLoading(false);
            return result;
        } catch (err) {
            setError(err);
            setIsLoading(false);
            throw err;
        }
    }, [wrappedFetch]);

    useEffect(() => {
        if (!hasFetched.current) {
            hasFetched.current = true;
            // Attempt to fetch presence. It might fail if the access token matches a different account, so we refresh in that case
            fetchPresence().catch(err => {
                console.error('Failed to fetch presence:', err);
                // if the error is 401 Unauthorized, we can attempt to fetch a new token and retry once
                if (err.message.includes('401')) {

                    console.log("Refreshing token and retrying presence fetch due to 401 error");

                    // clear presence cache and stored tokens
                    presenceCache = null;
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('authTokenExpiresAt');
                    tokenCache = null;
                    tokenExpiresAt = null;
                    setToken(null);

                    fetchToken().then(() => {
                        // After fetching a new token, try fetching presence again
                        fetchPresence().catch(err => {
                            console.error('Retrying presence fetch failed:', err);
                        });
                    }).catch(tokenErr => {
                        console.error('Failed to fetch token during presence retry:', tokenErr);
                    });
                }
            });
        }

        // Initialize token management
        if (!hasTokenFetched.current) {
            hasTokenFetched.current = true;

            // Initialize token on mount - this will only fetch if needed
            ensureValidToken().catch(err => {
                console.error('Initial token validation failed:', err);
            });
        }
    }, [ensureValidToken, fetchPresence, fetchToken]);

    const value = useMemo(() => ({
        fetch: wrappedFetch,
        DASHBOARD_URL,
        API_URL,
        account,
        channel,
        isLoading,
        error,
        token,
        fetchPresence,
        fetchToken,
        ensureValidToken,
    }), [
        wrappedFetch,
        DASHBOARD_URL,
        API_URL,
        account,
        channel,
        isLoading,
        error,
        token,
        fetchPresence,
        fetchToken,
        ensureValidToken,
    ]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

// Custom hook to use AuthContext
export function useAuth() {
    return useContext(AuthContext);
}

// For testing - reset global state
export function resetAuthState() {
    presenceCache = null;
    presencePromise = null;
    tokenCache = null;
    tokenPromise = null;
    tokenExpiresAt = null;
    suspensionRedirected = false;
}
